import type { Availability, Coverage, Finding, SourceKind } from '../api/types';
import { availabilityMeta, evidenceClassLabel, formatDate, sourceKindLabel } from '../lib/format';

const KINDS: SourceKind[] = ['discord', 'notion', 'repository', 'map', 'manual'];
const AVAILABILITY: Availability[] = ['available', 'metadata_only', 'missing', 'deleted', 'forbidden', 'error'];

function humanise(key: string): string {
  return key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (first) => first.toUpperCase());
}

/**
 * Source kind × availability, counted by the API over sources visible to you.
 * Missing, deleted, forbidden and failed acquisitions stay visible as gaps.
 */
export function SourceCoverageMatrix({ coverage }: { coverage: Coverage }) {
  const imported = coverage.imported;
  if (imported === null || imported === undefined) {
    return <p className="muted">Imported source counts are not available for your account.</p>;
  }
  const cells = imported.byKindAvailability ?? [];
  const count = (kind: SourceKind, availability: Availability) =>
    cells.find((cell) => cell.kind === kind && cell.availability === availability)?.count ?? 0;
  const kinds = KINDS.filter((kind) => cells.some((cell) => cell.kind === kind));
  const columns = AVAILABILITY;

  return (
    <div className="matrix-wrap" tabIndex={0} role="region" aria-label="Imported sources by kind and availability">
      <table className="matrix">
        <caption>
          Imported sources you can see, by kind and availability ({imported.sources} sources
          {imported.versions !== undefined ? `, ${String(imported.versions)} stored versions` : ''}).
        </caption>
        <thead>
          <tr>
            <th scope="col">Source kind</th>
            {columns.map((availability) => (
              <th key={availability} scope="col" className={`matrix__head matrix__head--${availabilityMeta[availability].tone}`}>
                {availabilityMeta[availability].label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {kinds.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="muted">
                No sources have been imported yet.
              </td>
            </tr>
          ) : (
            kinds.map((kind) => (
              <tr key={kind}>
                <th scope="row">{sourceKindLabel[kind]}</th>
                {columns.map((availability) => {
                  const value = count(kind, availability);
                  return (
                    <td key={availability} className={value === 0 ? 'matrix__cell matrix__cell--zero' : 'matrix__cell'}>
                      {value}
                    </td>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
      {(imported.unknownCreatedAt !== undefined || imported.copied !== undefined || imported.bot !== undefined) && (
        <p className="matrix__note">
          Of the stored versions, {imported.unknownCreatedAt ?? 'an unknown number'} have no established creation date,{' '}
          {imported.copied ?? 'an unknown number'} are marked as copies and {imported.bot ?? 'an unknown number'} as bot
          output. Copies and bot records are never counted as independent human voices.
        </p>
      )}
    </div>
  );
}

/** Dated acquisition and sampling reports. Their numbers are reported history, never live counters. */
export function HistoricalCoverage({ coverage }: { coverage: Coverage }) {
  const reports = coverage.historical?.historicalReported ?? [];
  if (coverage.historical === null || coverage.historical === undefined) {
    return (
      <p className="muted">
        Historical archive totals are withheld because some underlying sources are outside your access scope.
      </p>
    );
  }
  if (reports.length === 0) return <p className="muted">No dated coverage reports have been recorded.</p>;
  return (
    <div className="history-reports">
      {reports.map((report) => (
        <section key={report.id} className="history-report" aria-labelledby={`hr-${report.id}`}>
          <p className="eyebrow">Reported {formatDate(report.observedOn)}</p>
          <h3 id={`hr-${report.id}`} className="history-report__title">
            {report.basis ?? 'Coverage report'}
          </h3>
          {report.counts !== undefined && (
            <dl className="figures">
              {Object.entries(report.counts).map(([key, value]) => (
                <div key={key} className="figures__item">
                  <dt>{humanise(key)}</dt>
                  <dd>{value === null ? 'Not recorded' : value.toLocaleString('en-GB')}</dd>
                </div>
              ))}
            </dl>
          )}
          {(report.limits ?? []).length > 0 && (
            <ul className="point-list point-list--limits">
              {(report.limits ?? []).map((limit) => (
                <li key={limit}>{limit}</li>
              ))}
            </ul>
          )}
          {report.sourcePath !== undefined && <p className="muted small">Source: {report.sourcePath}</p>}
        </section>
      ))}
    </div>
  );
}

export function GapList({ coverage }: { coverage: Coverage }) {
  const gaps = coverage.gaps ?? [];
  if (gaps.length === 0) return <p className="muted">No recovery or review gaps are recorded for your access scope.</p>;
  return (
    <ul className="gap-list">
      {gaps.map((gap) => (
        <li key={gap.id ?? gap.title} className="gap">
          <p className="gap__title">
            {gap.title}
            {gap.state !== undefined && <span className="tag tag--gap">{gap.state}</span>}
          </p>
          {gap.scope !== undefined && <p className="gap__scope">{gap.scope}</p>}
          {gap.implication !== undefined && <p className="gap__implication">{gap.implication}</p>}
        </li>
      ))}
    </ul>
  );
}

/**
 * Chapters × evidence classes: how many visible findings in each chapter cite
 * each kind of evidence. Counted directly from the findings the API returned.
 */
export function EvidenceMatrix({ findings, chapters }: { findings: Finding[]; chapters: { id: string; title: string }[] }) {
  const classes = [...new Set(findings.flatMap((finding) => finding.evidence.map((link) => link.metadata?.evidenceClass ?? '')))]
    .filter((value) => value !== '')
    .sort();
  if (findings.length === 0 || classes.length === 0) {
    return <p className="muted">No findings with classified evidence match the current filters.</p>;
  }
  const rows = chapters.filter((chapter) => findings.some((finding) => finding.chapter === chapter.id));
  return (
    <div className="matrix-wrap" tabIndex={0} role="region" aria-label="Findings citing each evidence class, by chapter">
      <table className="matrix matrix--dense">
        <caption>Findings in view that cite each class of evidence, by chapter.</caption>
        <thead>
          <tr>
            <th scope="col">Chapter</th>
            {classes.map((value) => (
              <th key={value} scope="col">
                {evidenceClassLabel(value)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((chapter) => {
            const inChapter = findings.filter((finding) => finding.chapter === chapter.id);
            return (
              <tr key={chapter.id}>
                <th scope="row">
                  {chapter.title}
                  <span className="muted small"> ({inChapter.length})</span>
                </th>
                {classes.map((value) => {
                  const n = inChapter.filter((finding) =>
                    finding.evidence.some((link) => link.metadata?.evidenceClass === value),
                  ).length;
                  return (
                    <td key={value} className={n === 0 ? 'matrix__cell matrix__cell--zero' : 'matrix__cell'}>
                      {n}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
